import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { VERIFY_ROUTE } from '@/routes/constants';
import QAModeIndicator from '@/components/QAModeIndicator';
import { IS_PROD } from '@/config/featureFlags';

export default function AppShell({ children }) {
  const { pathname } = useLocation();
  const authPrefixes = ['/login', '/signup', '/reset-password', VERIFY_ROUTE, '/verify-email'];
  const isAuth = authPrefixes.some((prefix) => pathname.startsWith(prefix));

  // Support both legacy prop-based composition and the data-router Outlet pattern
  const content = children ?? <Outlet />;

  // Ensure view resets properly when navigating between auth pages
  useEffect(() => {
    if (isAuth) {
      try { window.scrollTo(0, 0); } catch { /* ignore */ }
    }
  }, [isAuth, pathname]);

  if (isAuth) {
    // Auth pages: render in an isolated, high z-index fixed container so nothing can overlay/block interactions
    return (
      <div
        className="fixed inset-0 z-[60] overflow-auto isolate bg-transparent text-slate-100"
      >
        <main className="pointer-events-auto flex min-h-screen items-center justify-center p-4">
          {content}
        </main>
      </div>
    );
  }

  // App pages: Single transparent shell, background from body in index.css
  return (
    <div data-testid="app-root" data-app-shell="true" className="relative min-h-screen bg-transparent text-slate-100">
      {/* BOOT MARKER - Proves AppShell rendered (Step B) */}
      <div data-testid="boot.marker" style={{ display: 'none' }}>boot-marker</div>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(30, 41, 59, 0.95)',
            color: '#f8fafc',
            border: '1px solid rgba(71, 85, 105, 0.5)',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(8px)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <QAModeIndicator />
      {content}
    </div>
  );
}
